import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useT } from "@/i18n/I18nContext";

const SOURCES = [
  { value: "organic", icon: "🔍" },
  { value: "google_ads", icon: "📣" },
  { value: "doctor", icon: "🩺" },
  { value: "co_counsel", icon: "⚖️" },
  { value: "client_referral", icon: "👥" },
  { value: "billboard", icon: "🪧" },
  { value: "social", icon: "📱" },
  { value: "other", icon: "✨" },
];

/**
 * "How'd you hear about us?" picker for the public intake form.
 * Emits onChange({ type, referrerId, hint }) so the parent can include those
 * fields in the /api/leads payload.
 *
 * Loads the public referrer directory once (no PII exposed by /referrers/public).
 */
export default function ReferralSourceSelect({ value, onChange }) {
  const t = useT();
  const [referrers, setReferrers] = useState([]);
  const [selectedType, setSelectedType] = useState(value?.type || "");
  const [referrerId, setReferrerId] = useState(value?.referrerId || "");
  const [hint, setHint] = useState(value?.hint || "");

  useEffect(() => {
    api
      .get("/referrers/public")
      .then((r) => setReferrers(r.data || []))
      .catch(() => setReferrers([]));
  }, []);

  const update = (next) => {
    onChange?.({
      type: next.type ?? selectedType,
      referrerId: next.referrerId ?? referrerId,
      hint: next.hint ?? hint,
    });
  };

  const pickType = (t2) => {
    setSelectedType(t2);
    // reset sub-fields when category changes
    setReferrerId("");
    setHint("");
    update({ type: t2, referrerId: "", hint: "" });
  };

  const matchedReferrers = referrers.filter((r) => {
    if (selectedType === "doctor") return r.type === "doctor";
    if (selectedType === "co_counsel") return r.type === "co_counsel";
    if (selectedType === "client_referral") return r.type === "past_client";
    return false;
  });

  const needsDetail =
    selectedType && ["doctor", "co_counsel", "client_referral", "other"].includes(selectedType);

  return (
    <div data-testid="referral-source-select">
      <label className="label-cap">{t("intake.referral.label")}</label>
      <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
        {SOURCES.map((s) => (
          <button
            key={s.value}
            type="button"
            onClick={() => pickType(s.value)}
            className={`flex flex-col items-center gap-1 rounded-lg border px-2 py-3 text-xs font-bold transition ${
              selectedType === s.value
                ? "border-[#11322A] bg-[#11322A] text-white"
                : "border-[#E0E4E2] bg-white text-[#5C6B68] hover:border-[#11322A] hover:text-[#11322A]"
            }`}
            data-testid={`referral-source-${s.value}`}
          >
            <span className="text-base" aria-hidden>{s.icon}</span>
            <span className="leading-tight text-center">{t(`intake.referral.${s.value}`)}</span>
          </button>
        ))}
      </div>

      {needsDetail && (
        <div className="mt-3 space-y-2">
          {matchedReferrers.length > 0 && (
            <div>
              <select
                value={referrerId}
                onChange={(e) => {
                  setReferrerId(e.target.value);
                  setHint("");
                  update({ referrerId: e.target.value, hint: "" });
                }}
                className="input-field"
                data-testid="referral-source-dropdown"
              >
                <option value="">{t("intake.referral.pick_one")}</option>
                {matchedReferrers.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}{r.firm_or_practice ? ` · ${r.firm_or_practice}` : ""}
                  </option>
                ))}
                <option value="__other__">{t("intake.referral.not_listed")}</option>
              </select>
            </div>
          )}
          {(referrerId === "__other__" || matchedReferrers.length === 0) && (
            <input
              value={hint}
              onChange={(e) => {
                setHint(e.target.value);
                setReferrerId("__other__");
                update({ referrerId: "", hint: e.target.value });
              }}
              placeholder={t("intake.referral.hint_placeholder")}
              className="input-field"
              data-testid="referral-source-hint"
            />
          )}
        </div>
      )}
    </div>
  );
}
